import React, { useEffect, useState, useCallback } from "react";
import { styled } from "@mui/material/styles";
import { Autocomplete, TextField } from "@mui/material";
import { AutocompleteRenderOptionState, createFilterOptions } from "@mui/material/Autocomplete";
import ModalTwoButtons from "../components/Modal/ModalTwoButtons";
import UserApi from "../api/Admin/User";
import { GlobalSecondaryButton } from "../pages/styles";
import { User } from "../api/types";

const autoCompleteFontSize = 14;

type RequestStatus = "idle" | "loading" | "success" | "error";

const extractNamesFromSearchValue = (searchValue: string) => {
  if (searchValue.includes("@")) {
    return { firstName: null, lastName: null };
  }

  if (searchValue.includes(",")) {
    const [lastName, firstName] = searchValue.split(",");
    return {
      firstName: firstName?.trim() || null,
      lastName: lastName?.trim() || null,
    };
  }

  return { firstName: null, lastName: searchValue.trim() };
};

const StyledAutocomplete = styled(Autocomplete<User, false, false, false>)({
  "& .MuiAutocomplete-input, & .MuiInputLabel-root": {
    fontSize: autoCompleteFontSize,
  },
}) as typeof Autocomplete<User, false, false, false>;

const Option = styled("li")({
  fontSize: autoCompleteFontSize,
});

const filterOptions = createFilterOptions<User>({
  matchFrom: "any",
  limit: 100,
});

interface UserLookupModalProps {
  display: boolean;
  initialSearchValue?: string;
  onOk: (email: User) => void;
  onCancel?: () => void;
}

export const UserLookupModal: React.FC<UserLookupModalProps> = ({
  display,
  initialSearchValue = "",
  onOk,
  onCancel = () => {},
}) => {
  const [avaliableUsers, setAvaliableUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [firstNameToSearch, setFirstNameToSearch] = useState<string | null>(null);
  const [lastNameToSearch, setLastNameToSearch] = useState<string | null>(initialSearchValue);
  const [status, setStatus] = useState<RequestStatus>("idle");

  const reset = () => {
    setAvaliableUsers([]);
    setSelectedUser(null);
    setFirstNameToSearch(null);
    setLastNameToSearch(null);
    setStatus("idle");
  };

  const handleOkClick = () => {
    if (selectedUser === null) return;
    onOk(selectedUser);
    reset();
  };
  const handleCancelClick = () => {
    reset();
    onCancel();
  };

  const searchForUser = useCallback(async () => {
    setStatus("loading");
    setSelectedUser(null);
    let cancelled = false;

    const trimmedFirstName = firstNameToSearch?.trim() || "";
    const trimmedLastName = lastNameToSearch?.trim() || "";

    try {
      let results: User[] = [];

      if (trimmedFirstName && trimmedLastName) {
        results = await UserApi.searchByFirstAndLastName(firstNameToSearch!, lastNameToSearch!);
      } else if (trimmedLastName) {
        results = await UserApi.searchByLastName(lastNameToSearch!);
      } else if (trimmedFirstName) {
        results = await UserApi.searchByFirstName(firstNameToSearch!);
      }

      if (cancelled) return;

      setAvaliableUsers(results);
      if (results.length === 1) {
        setSelectedUser(results[0]);
      }
      setStatus("idle");
    } catch (error) {
      if (!cancelled) {
        console.error("User search failed:", error);
        setStatus("error");
      }
    }
  }, [firstNameToSearch, lastNameToSearch]);

  const handleSearchAgainClick = searchForUser;

  useEffect(() => {
    if (initialSearchValue && initialSearchValue !== "") {
      searchForUser();
    }
  }, [initialSearchValue, searchForUser]);

  useEffect(() => {
    if (!display) return;

    if (initialSearchValue) {
      const { firstName, lastName } = extractNamesFromSearchValue(initialSearchValue);
      setFirstNameToSearch(firstName);
      setLastNameToSearch(lastName);
    } else {
      // Reset when modal opens with no initial value
      setFirstNameToSearch(null);
      setLastNameToSearch(null);
    }
  }, [display, initialSearchValue]);

  const handleKeyPress = async (event: React.KeyboardEvent<HTMLDivElement>): Promise<void> => {
    if (event.key === "Enter") {
      event.preventDefault();
      searchForUser();
    }
  };

  return (
    <ModalTwoButtons
      title={"Search for User"}
      button1Text={"Ok"}
      button1Action={handleOkClick}
      isButton1Disabled={selectedUser === null}
      button2Text={"Cancel"}
      button2Action={handleCancelClick}
      isButton2Disabled={undefined}
      open={display}
      setOpen={undefined}
      setClose={undefined}>
      <label>
        {"Please select your desired user from the list below, or modify the search criteria."}
      </label>

      <div style={{ display: "flex" }}>
        <TextField
          id="outlined-normal"
          style={{ width: "40%", marginRight: "1rem" }}
          size="small"
          margin="normal"
          variant="outlined"
          label="First Name"
          InputLabelProps={{ shrink: true }}
          inputProps={{ maxLength: 50 }}
          multiline
          onChange={(e) => setFirstNameToSearch(e.target.value)}
          value={firstNameToSearch === null ? "" : firstNameToSearch}
          onKeyDown={handleKeyPress}
        />

        <TextField
          id="outlined-normal"
          style={{ width: "40%", marginRight: "1rem" }}
          size="small"
          margin="normal"
          variant="outlined"
          label="Last Name"
          InputLabelProps={{ shrink: true }}
          inputProps={{ maxLength: 50 }}
          multiline
          onChange={(e) => setLastNameToSearch(e.target.value)}
          value={lastNameToSearch === null ? "" : lastNameToSearch}
          onKeyDown={handleKeyPress}
        />

        <GlobalSecondaryButton
          style={{ marginTop: "1.1rem", width: "8rem" }}
          disabled={status === "loading"}
          onClick={handleSearchAgainClick}>
          {"Search Again"}
        </GlobalSecondaryButton>
      </div>

      <div style={{ display: "flex" }}>
        <StyledAutocomplete
          renderOption={(
            props: React.HTMLAttributes<HTMLLIElement>,
            option: User,
            state: AutocompleteRenderOptionState,
          ) => <Option {...props}>{`${option.displayName}`}</Option>}
          noOptionsText={"No Users Found..."}
          options={avaliableUsers.filter((e) => e.mail !== null)}
          getOptionLabel={(option) => `${option.displayName}`}
          onChange={(e, value) => setSelectedUser(value)}
          autoHighlight
          filterOptions={filterOptions}
          value={selectedUser}
          isOptionEqualToValue={(option, value) => value.displayName === option.displayName}
          style={{ width: "70%" }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              margin="normal"
              label="Select User"
              InputLabelProps={{ shrink: true }}
              InputProps={{ ...params.InputProps }}
            />
          )}
        />
        <label style={{ marginLeft: "1rem", marginTop: "1rem" }}>{`***${
          avaliableUsers.filter((e) => e.mail !== null).length
        } ${
          avaliableUsers.filter((e) => e.mail !== null).length > 1 ? "users" : "user"
        } found!***`}</label>
      </div>
    </ModalTwoButtons>
  );
};
